import React from 'react';
import { navigate, Link } from '@util/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Navbar,
  Nav,
  NavItem,
  NavLink,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  navbarTogglerIconClass,
} from '@ui/components';

import { useUser } from '@util/providers/AuthProvider';

import LogInButton from '@components/Account/LogInButton';
import UserMenu from '@components/Account/UserMenu';

import logo from '../images/logo_boardmaker.png';
import RegionSelector from '@components/RegionSelector';
import {
  BANNER_HEIGHT,
  IS_BANNER_VISIBLE,
} from '@components/EnvironmentBanner';

const Header = (props) => {
  return (
    <FixedHeader>
      <Navbar
        expand="xs"
        className="bg-body text-gray-900 py-0 border-b border-gray-300"
      >
        <div className="mx-auto w-full px-3.75 sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl flex flex-row flex-nowrap justify-between items-center">
          <a
            className="inline-block sm:hidden sm:pl-4 py-2 text-inherit whitespace-nowrap"
            href="/"
            style={{ width: 180 }}
          >
            <img src={logo} alt="Boardmaker" className="max-w-full h-auto" />
          </a>
          <a
            className="hidden sm:inline-block pl-0 sm:pl-4 md:pl-0 py-2 text-inherit whitespace-nowrap"
            href="/"
            style={{ width: 230 }}
          >
            <img src={logo} alt="Boardmaker" className="max-w-full h-auto" />
          </a>
          {!props.isRetrievingUserInfo && (
            <>
              <Nav className="ml-auto md:hidden" navbar>
                <MobileMenu />
              </Nav>
              <Nav className="ml-auto hidden md:flex" navbar>
                <DesktopMenu />
              </Nav>
            </>
          )}
        </div>
      </Navbar>
    </FixedHeader>
  );
};

Header.propTypes = {
  siteTitle: PropTypes.string,
};

Header.defaultProps = {
  siteTitle: ``,
};

function DesktopMenu() {
  return (
    <>
      <RegionSelector />
      <BMOLink />
      <DownloadsLink />
      <UserMenu />
    </>
  );
}

function MobileMenu() {
  const { t } = useTranslation();
  const { ssoUser, signOut } = useUser();
  return (
    <>
      <RegionSelector mobile />
      <UncontrolledDropdown nav inNavbar className="block md:hidden">
        <DropdownToggle
          nav
          className="p-0 border-0 bg-transparent [&>span.toggle-caret]:hidden"
          aria-label="Toggle navigation"
        >
          <span className={navbarTogglerIconClass}></span>
        </DropdownToggle>
        <DropdownMenu end className="mb-4 md:mb-0 py-4">
          {ssoUser && (
            <DropdownItem header className="pl-4">
              {ssoUser.given_name}
            </DropdownItem>
          )}
          <BMOLink mobile />
          <DownloadsLink mobile />

          {ssoUser ? (
            <DropdownItem data-testid="MobileHeaderLogOut" onClick={signOut}>
              {t('account.logOut')}
            </DropdownItem>
          ) : (
            <LogInButton
              className="inline-block cursor-pointer select-none rounded border border-transparent px-5 py-2.5 text-base text-center align-middle leading-normal font-normal transition-colors duration-150 ease-in-out focus:outline-none bg-transparent text-info hover:underline"
              mobile
            />
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    </>
  );
}

function BMOLink({ mobile = false }) {
  const { t } = useTranslation();
  const { bmoUser } = useUser();

  const linkText = t('boardmakerHome');
  const handleClick = () => {
    navigate('/redirect', {
      state: {
        extension: bmoUser ? '/You/Home' : '',
      },
    });
  };

  if (mobile)
    return (
      <DropdownItem
        data-testid="MobileHeaderBoardmakerHome"
        onClick={handleClick}
      >
        {' '}
        {linkText}
      </DropdownItem>
    );

  return (
    <NavItem className="hidden md:block">
      <NavLink
        data-testid="HeaderBoardmakerHome"
        tag="button"
        onClick={handleClick}
      >
        {linkText}
      </NavLink>
    </NavItem>
  );
}

function DownloadsLink({ mobile = false }) {
  const { t } = useTranslation();
  const linkText = t('action.downloads');

  if (mobile) {
    return (
      <Link
        data-testid="MobileHeaderDownloads"
        to="/downloads"
        className="block w-full px-6 py-1 text-gray-900 no-underline cursor-pointer hover:bg-info hover:text-gray-100"
      >
        {linkText}
      </Link>
    );
  }

  return (
    <NavItem>
      <Link
        data-testid="HeaderDownloads"
        to="/downloads"
        className="block px-3 py-2 text-info no-underline whitespace-nowrap hover:underline"
      >
        {linkText}
      </Link>
    </NavItem>
  );
}

const FixedHeader = styled.div`
  position: fixed;
  top: ${IS_BANNER_VISIBLE ? `${BANNER_HEIGHT}px` : '0'};
  width: 100%;
  background-color: #fafafa;
  border-bottom: 1px solid #e6e7e8;
  z-index: 1001;
`;

export default Header;
